// v5.7.0 · Repositorio privado en Google Drive — exclusivo del Super Administrador.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, Upload, Download, Trash2, Search, Folder, FolderOpen, FileText, Loader2, AlertCircle, ExternalLink, Pencil, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const fmtDate = (s) => { try { return new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };

export default function AdminRepositorioSuper() {
  const { user } = useAuth();
  const isSuper = user?.role === "admin" && !!user?.is_super_admin;
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/super-repository", { params: { ...(folder ? { folder } : {}), ...(q ? { q } : {}) } }),
        api.get("/super-repository/folders"),
      ]);
      setItems(a.data || []); setFolders(b.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar el repositorio");
    } finally { setLoading(false); }
  }, [folder, q]);
  useEffect(() => { if (isSuper) { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); } }, [load, isSuper, q]);
  useEffect(() => {
    if (!items.some((i) => i.status === "uploading")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [items, load]);

  const total = useMemo(() => folders.reduce((a, f) => a + f.count, 0), [folders]);

  const download = async (it) => {
    try {
      const r = await api.get(`/super-repository/${it.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = it.file_name || "archivo"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("No se pudo descargar el archivo"); }
  };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar «${it.title}»? También se envía a la papelera de Drive.`)) return;
    try { await api.delete(`/super-repository/${it.id}`); toast.success("Archivo eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar"); }
  };

  if (!isSuper) {
    return <div className="jasq-card p-8 text-center text-[#6E7591]" data-testid="repo-denied"><Lock className="mx-auto mb-2" /> Este repositorio es solo para el Super Administrador.</div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-repositorio-super">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-[#A43790]"><Lock size={12} /> Solo Super Administrador</span>
          <h1 className="font-display text-3xl font-extrabold text-[#1F2547]">Repositorio en Drive</h1>
          <p className="text-sm text-[#6E7591]">Archivos privados guardados en Google Drive. No se comparten por enlace público.</p>
        </div>
        <button onClick={() => setShowUpload(true)} className="jasq-btn-primary inline-flex items-center gap-2" data-testid="repo-upload-btn"><Upload size={16} /> Subir archivo</button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="repo-folders">
        <button onClick={() => setFolder("")} className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 inline-flex items-center gap-1.5 ${!folder ? "border-[#A43790] bg-[#A43790] text-white" : "border-[#E1E5F0] text-[#4C5478]"}`}>
          <FolderOpen size={12} /> Todas ({total})
        </button>
        {folders.map((f) => (
          <button key={f.name} onClick={() => setFolder(f.name)} className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 inline-flex items-center gap-1.5 ${folder === f.name ? "border-[#A43790] bg-[#A43790] text-white" : "border-[#E1E5F0] text-[#4C5478]"}`}>
            <Folder size={12} /> {f.name} ({f.count})
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7591]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="jasq-input !pl-8 !py-1.5 !text-xs" data-testid="repo-search" />
        </div>
      </div>

      {loading && !items.length ? (
        <div className="py-10 text-center text-[#6E7591]"><Loader2 className="animate-spin inline mr-2" size={16} /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="jasq-card p-10 text-center text-[#6E7591]" data-testid="repo-empty">Aún no hay archivos. Usa «Subir archivo».</div>
      ) : (
        <div className="jasq-card divide-y divide-[#E1E5F0]">
          {items.map((it) => (
            <div key={it.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`repo-item-${it.id}`}>
              <FileText size={20} className="text-[#A43790] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-[#1F2547] truncate">{it.title}</p>
                <p className="text-xs text-[#6E7591] truncate">
                  <Folder size={10} className="inline -mt-0.5" /> {it.folder} · {it.file_name} · {fmtSize(it.file_size)} · {fmtDate(it.created_at)}
                </p>
                {it.description && <p className="text-xs text-[#4C5478] mt-0.5">{it.description}</p>}
                {it.status === "failed" && <p className="text-xs text-[#A52258] mt-0.5 inline-flex items-center gap-1"><AlertCircle size={11} /> {it.error || "La subida falló"}</p>}
              </div>
              {it.status === "uploading" && <span className="text-xs font-bold text-[#9C7A18] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Subiendo a Drive…</span>}
              {it.status === "ready" && (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => download(it)} className="jasq-btn-ghost text-xs inline-flex items-center gap-1" data-testid={`repo-dl-${it.id}`}><Download size={13} /> Descargar</button>
                  {it.web_view_link && <a href={it.web_view_link} target="_blank" rel="noreferrer" className="jasq-btn-ghost text-xs inline-flex items-center gap-1"><ExternalLink size={13} /> Drive</a>}
                </div>
              )}
              <button onClick={() => setEditing(it)} className="p-2 text-[#6E7591] hover:text-[#2C49A0]" title="Editar"><Pencil size={15} /></button>
              <button onClick={() => remove(it)} className="p-2 text-[#6E7591] hover:text-[#A52258]" title="Eliminar" data-testid={`repo-del-${it.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {showUpload && <UploadModal folders={folders} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
      {editing && <EditModal item={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-display text-lg font-extrabold text-[#1F2547]">{title}</h3><button onClick={onClose}><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
}

function UploadModal({ folders, onClose, onDone }) {
  const [title, setTitle] = useState(""); const [folder, setFolder] = useState("General");
  const [description, setDescription] = useState(""); const [file, setFile] = useState(null); const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file || !title.trim()) { toast.error("Indica un título y elige un archivo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title); fd.append("folder", folder || "General"); fd.append("description", description); fd.append("file", file);
      await api.post("/super-repository/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      toast.success("Archivo recibido: se sube a Drive en segundo plano");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo subir"); setBusy(false); }
  };
  return (
    <Modal title="Subir archivo" onClose={onClose}>
      <input className="jasq-input w-full" placeholder="Título" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="repo-up-title" />
      <input className="jasq-input w-full" list="repo-folder-list" placeholder="Carpeta (p. ej. Contratos)" value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="repo-up-folder" />
      <datalist id="repo-folder-list">{folders.map((f) => <option key={f.name} value={f.name} />)}</datalist>
      <textarea className="jasq-input w-full" rows={2} placeholder="Descripción (opcional)" value={description} onChange={(e) => setDescription(e.target.value)} />
      <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="repo-up-file" />
      <p className="text-[11px] text-[#6E7591]">Máximo 200 MB. La carpeta se crea sola en Drive si no existe.</p>
      <div className="flex justify-end gap-2"><button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
        <button onClick={submit} disabled={busy} className="jasq-btn-primary" data-testid="repo-up-submit">{busy ? "Enviando…" : "Subir"}</button></div>
    </Modal>
  );
}

function EditModal({ item, onClose, onDone }) {
  const [title, setTitle] = useState(item.title); const [description, setDescription] = useState(item.description || ""); const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/super-repository/${item.id}`, { title, description }); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar"); setBusy(false); }
  };
  return (
    <Modal title="Editar archivo" onClose={onClose}>
      <input className="jasq-input w-full" value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className="jasq-input w-full" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      <div className="flex justify-end gap-2"><button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
        <button onClick={save} disabled={busy} className="jasq-btn-primary">Guardar</button></div>
    </Modal>
  );
}
